import React from "react";
import { Box, Chip, IconButton, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface AvailableDatePickerProps {
  dates: string[];
  onChange: (dates: string[]) => void;
}

const AvailableDatePicker = ({ dates, onChange }: AvailableDatePickerProps) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const handleAddDate = (date: moment.Moment | null) => {
    if (!date) return;
    const isoDate = date.format("YYYY-MM-DD");
    if (!dates.includes(isoDate)) {
      onChange([...dates, isoDate].sort());
    }
    setPickerOpen(false);
  };

  const handleRemoveDate = (dateToRemove: string) => {
    onChange(dates.filter((d) => d !== dateToRemove));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Date chips */}
      {dates.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {dates.map((date) => (
            <Chip
              key={date}
              label={formatUiDate(date)}
              onDelete={() => handleRemoveDate(date)}
              deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
              icon={<CalendarMonthIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: dt.radiusPill,
                backgroundColor: dt.blueGlow,
                color: dt.blue,
                border: `1px solid ${dt.blue}20`,
                "& .MuiChip-icon": { color: dt.blue },
                "& .MuiChip-deleteIcon": {
                  color: dt.blue,
                  "&:hover": { color: dt.blueHover },
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Add date button / picker */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          open={pickerOpen}
          onOpen={() => setPickerOpen(true)}
          onClose={() => setPickerOpen(false)}
          value={null}
          onChange={handleAddDate}
          minDate={moment()}
          shouldDisableDate={(date) => dates.includes(date.format("YYYY-MM-DD"))}
          slotProps={{
            textField: {
              size: "small",
              placeholder: translate("addAvailableDate"),
              onClick: () => setPickerOpen(true),
              InputProps: {
                readOnly: true,
                startAdornment: (
                  <AddIcon sx={{ fontSize: 18, color: dt.blue, mr: 0.5 }} />
                ),
              },
              sx: {
                maxWidth: "260px",
                "& .MuiOutlinedInput-root": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  borderRadius: dt.radiusSm,
                  cursor: "pointer",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default AvailableDatePicker;
